import type { Product } from '@/types/product';
import type { VendorAssociation } from '@/types/vendor-association';
import type { ProductUpdateCallback, StorageKeys } from '@/types/product-storage';

const STORAGE_KEYS: StorageKeys = {
  PRODUCTS: 'products',
  PURCHASE_HISTORY: 'purchase_history',
  VENDOR_ASSOCIATIONS: 'vendor_associations',
};

type AssociationUpdateCallback = (associations: VendorAssociation[]) => void;
const subscribers = new Set<ProductUpdateCallback>();
const associationSubscribers = new Set<AssociationUpdateCallback>();

// Product Management
export function getStoredProducts(): Product[] {
  const products = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
  return products ? JSON.parse(products) : [];
}

export function addStoredProduct(product: Product): Product {
  const products = getStoredProducts();
  products.push(product);
  localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(products));
  notifySubscribers(products);
  return product;
}

export function updateStoredProduct(product: Product): Product {
  const products = getStoredProducts();
  const index = products.findIndex((p: any) => p.id === product.id);
  if (index !== -1) {
    products[index] = product;
    localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(products));
    notifySubscribers(products);
  }
  return product;
}

export function deleteStoredProduct(productId: string) {
  const products = getStoredProducts();
  const filteredProducts = products.filter((p: any) => p.id !== productId);
  localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(filteredProducts));
  notifySubscribers(filteredProducts);
}

// Purchase History Management
export function getStoredPurchaseHistory() {
  const history = localStorage.getItem(STORAGE_KEYS.PURCHASE_HISTORY);
  return history ? JSON.parse(history) : [];
}

export function addStoredPurchase(purchase: unknown) {
  const history = getStoredPurchaseHistory();
  history.push(purchase);
  localStorage.setItem(STORAGE_KEYS.PURCHASE_HISTORY, JSON.stringify(history));
  return purchase;
}

// Vendor Associations Management
export function getStoredVendorAssociations(): VendorAssociation[] {
  try {
    const associations = localStorage.getItem(STORAGE_KEYS.VENDOR_ASSOCIATIONS);
    return associations ? JSON.parse(associations) : [];
  } catch (error) {
    console.error('Error loading vendor associations:', error);
    return [];
  }
}

export function addStoredVendorAssociation(association: VendorAssociation) {
  const associations = getStoredVendorAssociations();
  
  if (!association?.vendorId || !association?.productId) {
    throw new Error('Invalid association data: missing required fields');
  }
  
  const exists = associations.some(
    a => a.vendorId === association.vendorId && a.productId === association.productId
  );
  
  if (exists) {
    throw new Error('Association already exists');
  }
  
  const newAssociation = {
    ...association,
    createdAt: association.createdAt || new Date().toISOString(),
    updatedAt: association.updatedAt || new Date().toISOString(),
  };
  
  associations.push(newAssociation);
  localStorage.setItem(STORAGE_KEYS.VENDOR_ASSOCIATIONS, JSON.stringify(associations));
  notifyAssociationSubscribers(associations);
  return newAssociation;
}

export function updateStoredVendorAssociation(association: any) {
  const associations = getStoredVendorAssociations();
  const index = associations.findIndex(
    (a: any) => a.vendorId === association.vendorId && a.productId === association.productId
  );
  if (index !== -1) {
    associations[index] = association;
    localStorage.setItem(STORAGE_KEYS.VENDOR_ASSOCIATIONS, JSON.stringify(associations));
    notifyAssociationSubscribers(associations);
  }
  return association;
}

export function deleteStoredVendorAssociation(vendorId: string, productId: string) {
  const associations = getStoredVendorAssociations();
  const filteredAssociations = associations.filter(
    (a) => !(a.vendorId === vendorId && a.productId === productId)
  );
  localStorage.setItem(STORAGE_KEYS.VENDOR_ASSOCIATIONS, JSON.stringify(filteredAssociations));
  notifyAssociationSubscribers(filteredAssociations);
  console.log('Deleted vendor association:', { vendorId, productId });
}

function notifySubscribers(products: Product[]) {
  subscribers.forEach(callback => callback(products));
}

function notifyAssociationSubscribers(associations: VendorAssociation[]) {
  associationSubscribers.forEach(callback => callback(associations));
}

export function subscribeToProductUpdates(callback: ProductUpdateCallback) {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

export function subscribeToAssociationUpdates(callback: AssociationUpdateCallback) {
  associationSubscribers.add(callback);
  return () => {
    associationSubscribers.delete(callback);
  };
}

export function clearStoredVendorAssociations() {
  localStorage.removeItem(STORAGE_KEYS.VENDOR_ASSOCIATIONS);
  notifyAssociationSubscribers([]);
  console.log('Cleared all vendor associations');
}